/**
 * A `ShellScreen` plus its focus and any open dialog, turned into one draw
 * list and the hit test that inverts it. Same contract as `screen-view.ts`'s
 * for the Main Menu — both come out of one layout pass, so a control can never
 * be drawn somewhere the mouse does not find it.
 */

import type { ShellDialog, ShellScreen } from "../model/shell-screen.ts";
import { focusAt, interactiveWidgets } from "../model/shell-screen.ts";
import { boardChromeOps, type BoardSpec, boardRows } from "./board-chrome.ts";
import type { DrawOp, FontId } from "./draw-ops.ts";
import { panelOps, PANEL_SHEET } from "./panel-chrome.ts";
import { layoutPopup, popupOps, popupRowAt, type PopupContent } from "./popup-chrome.ts";
import type { FontAtlas } from "./text-metrics.ts";
import { widgetOps, type WidgetFonts } from "./widget-ops.ts";

export type ShellHit = { readonly kind: "widget" | "dialog"; readonly index: number };

export interface SurfaceView {
  readonly ops: readonly DrawOp[];
  hitTest(x: number, y: number): ShellHit | undefined;
}

export function fontOf(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const atlas = fonts.get(id);
  if (!atlas) throw new Error(`shell: font atlas ${id} was never loaded`);
  return atlas;
}

/** The emblem sprites a side-picker row carries at each end (`dialog-who-will-you-fly-for.png`). */
const ROW_EMBLEM_FRAME: Readonly<Record<string, number>> = { roundel: 16, cross: 17 };
const ROW_EMBLEM_WIDTH = 16;

function chromeOps(screen: ShellScreen, fonts: ReadonlyMap<FontId, FontAtlas>): DrawOp[] {
  // A chrome-less screen draws nothing of its own, so the frame keeps whatever
  // the surface was cleared to — transparent, for a dialog over the cockpit.
  if (screen.chrome.kind === "none") return [];
  const banner = fontOf(fonts, "FONT2");
  if (screen.chrome.kind === "panel") {
    return panelOps(screen.chrome.title, screen.chrome.emblem, banner, screen.chrome.background);
  }
  const { board, background } = screen.chrome;
  const spec: BoardSpec = {
    frame: board.frame,
    rows: boardRows(1),
    columns: 1,
    columnWidth: board.frame.w - 22,
    columnGap: 0,
    ridgeInsets: board.ridgeInsets,
    footerInsets: board.footerInsets,
  };
  const base: DrawOp[] = background ? [{ kind: "image", asset: background, x: 0, y: 0 }] : [];
  return [...base, ...boardChromeOps(spec, board.title, banner)];
}

/**
 * One place the popup's content is derived from its model, so the painter and
 * the hit test can never be handed different boards. They used to build the
 * object twice, which was harmless while it had three fields and stops being so
 * the moment one of them changes the layout — `minWidth` does.
 */
function popupContentOf(dialog: ShellDialog): PopupContent {
  return {
    title: dialog.title,
    body: dialog.body,
    labels: dialog.rows.map((row) => row.label),
    minWidth: dialog.minWidth,
    footer: dialog.footer,
  };
}

function dialogDrawOps(dialog: ShellDialog, fonts: ReadonlyMap<FontId, FontAtlas>): DrawOp[] {
  const popupFonts = { title: fontOf(fonts, "FONT1"), row: fontOf(fonts, "FONT3") };
  const content = popupContentOf(dialog);
  const layout = layoutPopup(content, popupFonts);
  const ops = popupOps(content, layout, dialog.focus, popupFonts);
  dialog.rows.forEach((row, index) => {
    const frame = ROW_EMBLEM_FRAME[row.emblem];
    const rect = layout.rows[index];
    if (frame === undefined || !rect) return;
    const y = rect.y - 2;
    ops.push({ kind: "sprite", sheet: PANEL_SHEET, frame, x: rect.x - ROW_EMBLEM_WIDTH - 1, y, flipH: false });
    ops.push({ kind: "sprite", sheet: PANEL_SHEET, frame, x: rect.x + rect.w + 1, y, flipH: false });
  });
  return ops;
}

function dialogHit(dialog: ShellDialog, fonts: ReadonlyMap<FontId, FontAtlas>, x: number, y: number): number | undefined {
  const popupFonts = { title: fontOf(fonts, "FONT1"), row: fontOf(fonts, "FONT3") };
  const layout = layoutPopup(
    popupContentOf(dialog),
    popupFonts,
  );
  return popupRowAt(layout, x, y);
}

export function buildSurfaceView(
  screen: ShellScreen,
  focus: number,
  dialog: ShellDialog | undefined,
  fonts: ReadonlyMap<FontId, FontAtlas>,
): SurfaceView {
  const widgetFonts: WidgetFonts = { heading: fontOf(fonts, "FONT1"), small: fontOf(fonts, "FONT3") };
  const controls = interactiveWidgets(screen);
  const ops: DrawOp[] = chromeOps(screen, fonts);
  for (const widget of screen.widgets) {
    const index = controls.indexOf(widget as (typeof controls)[number]);
    ops.push(...widgetOps(widget, widgetFonts, !dialog && index >= 0 && index === focus));
  }
  if (dialog) ops.push(...dialogDrawOps(dialog, fonts));
  return {
    ops,
    hitTest(x, y) {
      if (dialog) {
        const row = dialogHit(dialog, fonts, x, y);
        return row === undefined ? undefined : { kind: "dialog", index: row };
      }
      const index = focusAt(screen, x, y);
      return index === undefined ? undefined : { kind: "widget", index };
    },
  };
}
